import React, { useState } from 'react';

interface ResistanceTabProps {
  reportData?: any;
}

const ResistanceTab: React.FC<ResistanceTabProps> = ({ reportData }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  // Default data in case no report data is provided
  const resistanceClasses = reportData?.analysis_results?.pathogens?.resistance_classes || ["esbl"];
  
  const toggleSection = (sectionName: string) => {
    if (expandedSection === sectionName) {
      setExpandedSection(null);
    } else {
      setExpandedSection(sectionName);
    }
  };

  const handleExpandFirst = () => {
    if (resistanceClasses.length > 0) {
      setExpandedSection(resistanceClasses[0]);
    }
  };

  const handleCollapseAll = () => {
    setExpandedSection(null);
  };



  return (
    <div className="p-6">
      <div className="mb-8">
        {/* Resistance Profile Header with Expand/Collapse Buttons */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-text">Resistance Profile</h3>
          <div className="space-x-2 flex items-center">
            <button 
              onClick={handleExpandFirst}
              className="text-primary px-4 py-1 border border-primary rounded text-sm flex items-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Expand First
            </button>
            <button 
              onClick={handleCollapseAll}
              className="text-primary px-4 py-1 border border-primary rounded text-sm flex items-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Collapse All
            </button>
          </div>
        </div>

        {/* Resistance Profile Section */}
        <div className="border border-border rounded-md mb-6">
          <div className="p-4">
            <h3 className="text-lg font-semibold text-text mb-4">Resistance Profile</h3>
            
            {/* Chart */}
            <div className="h-64 w-full mb-6">
              <div className="w-full h-full flex flex-col">
              <div className="h-full w-full mb-6 overflow-hidden relative">
              <svg 
                    className="w-full h-full" 
                    viewBox="0 0 400 240" 
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <g transform="translate(60, 20)">
                      {/* Y-axis labels */}
                      <text x="-10" y="0" fontSize="12" textAnchor="end" dominantBaseline="middle">1</text>
                      <text x="-10" y="90" fontSize="12" textAnchor="end" dominantBaseline="middle">0.5</text>
                      <text x="-10" y="180" fontSize="12" textAnchor="end" dominantBaseline="middle">0</text>
                      
                      <text x="140" y="200" fontSize="12" textAnchor="middle">esbl</text>
                      <text x="140" y="220" fontSize="12" textAnchor="middle" fontWeight="medium">Resistance Class</text>

                      
                      {/* Y-axis label */}
                      <text x="-40" y="90" fontSize="12" textAnchor="middle" transform="rotate(-90, -40, 90)" fontWeight="medium">Count</text>
                      
                      {/* Background grid lines */}
                      <line x1="0" y1="0" x2="280" y2="0" stroke="#e5e5e5" strokeWidth="1" strokeDasharray="3,3" />
                      <line x1="0" y1="90" x2="280" y2="90" stroke="#e5e5e5" strokeWidth="1" strokeDasharray="3,3" />
                      <line x1="0" y1="180" x2="280" y2="180" stroke="#e5e5e5" strokeWidth="1" />
                      
                      {/* Chart bars */}
                      <rect x="120" y="60" width="40" height="120" rx="2" fill="#b68f98" />
                      
                      {/* Axes */}
                      <line x1="0" y1="0" x2="0" y2="180" stroke="#666" strokeWidth="1.5" />
                      <line x1="0" y1="180" x2="280" y2="180" stroke="#666" strokeWidth="1.5" />
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resistance Summary Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text mb-4">Resistance Summary:</h3>
          <p className="mb-4">This profile shows 1 resistance genes across 1 classes, indicating moderate antimicrobial resistance.</p>
          
          <h4 className="text-lg font-semibold text-text mb-2">Implications:</h4>
          <ul className="list-disc pl-8 mb-6 space-y-2">
            <li>May require combination therapy</li>
            <li>Consider alternative treatment options</li>
            <li>Monitor treatment response closely</li>
          </ul>
        </div>

        {/* ESBL Resistance Section with toggle */}
        <div className="border border-border rounded-md mb-6 overflow-hidden">
          <div 
            className="flex items-center p-4 bg-white border-l-4 border-red-400 cursor-pointer"
            onClick={() => toggleSection('esbl')}
          >
            <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center mr-3">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500"></div>
            </div>
            <div className="flex-grow">
              <span className="font-medium">Esbl Resistance</span>
            </div>
            <div className="ml-2">
              <span className="text-xs inline-block px-2 py-0.5 bg-red-100 text-red-800 rounded-full">1 gene</span>
            </div>
            <div className="ml-4">
              <svg 
                className={`h-5 w-5 transform ${expandedSection === 'esbl' ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {expandedSection === 'esbl' && (
            <div className="p-6 border-t border-gray-200">
              <div className="mb-4">
                <h4 className="font-medium mb-2">Detected Genes:</h4>
                <p>CTX-M</p>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium mb-2">Clinical Implications</h4>
                <p className="text-sm">
                  Extended-spectrum beta-lactamase (ESBL) resistance affects most beta-lactam antibiotics, including penicillins and cephalosporins. This significantly limits treatment options and may require the use of carbapenems or combination therapy.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Recommendations</h4>
                <ul className="list-none space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                      <svg className="h-3 w-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    Consider carbapenems for serious infections
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                      <svg className="h-3 w-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    Aminoglycosides or fluoroquinolones may be alternatives if susceptible
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                      <svg className="h-3 w-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    Combination therapy may be more effective
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 bg-green-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                      <svg className="h-3 w-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    Consult infectious disease specialist for complex cases
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

         
      </div>
    </div>
  );
};

export default ResistanceTab; 